import { t } from "@lingui/core/macro";
import invariant from "tiny-invariant";

import {
  CropCategory,
  CropSubcategory,
} from "@/generated/digitalnisklady.cz/graphql";

const categoriesWithSubcategories = new Set<CropCategory>([
  CropCategory.Fertilizer,
  CropCategory.PlantProtection,
]);

const hasSubcategories = (category: CropCategory): boolean =>
  categoriesWithSubcategories.has(category);

const subcategoryTranslations = new Map<CropSubcategory, () => string>();

subcategoryTranslations.set(CropSubcategory.Bioactivcz, () => t`BioAktivCZ`);
subcategoryTranslations.set(CropSubcategory.Calcareous, () => t`Vápenatá`);
subcategoryTranslations.set(CropSubcategory.Foliar, () => t`Listová`);
subcategoryTranslations.set(CropSubcategory.Magnesium, () => t`Hořečnatá`);
subcategoryTranslations.set(
  CropSubcategory.MultiComponent,
  () => t`Vícesložková`,
);
subcategoryTranslations.set(CropSubcategory.Nitrogen, () => t`Dusíkatá`);
subcategoryTranslations.set(
  CropSubcategory.NitrogenSulphur,
  () => t`Dusíkatá se sírou`,
);
subcategoryTranslations.set(CropSubcategory.Other, () => t`Ostatní`);
subcategoryTranslations.set(CropSubcategory.Phosphate, () => t`Fosforečná`);
subcategoryTranslations.set(CropSubcategory.Potassium, () => t`Draselná`);

subcategoryTranslations.set(CropSubcategory.Fungicides, () => t`Fungicidy`);
subcategoryTranslations.set(CropSubcategory.Herbicides, () => t`Herbicidy`);
subcategoryTranslations.set(CropSubcategory.Insecticides, () => t`Insekticidy`);
subcategoryTranslations.set(
  CropSubcategory.AuxiliaryPreparations,
  () => t`Pomocné přípravky`,
);
subcategoryTranslations.set(
  CropSubcategory.GrowthRegulators,
  () => t`Regulátory růstu`,
);
subcategoryTranslations.set(CropSubcategory.Rodenticides, () => t`Rodenticidy`);
subcategoryTranslations.set(CropSubcategory.Packages, () => t`Balíčky`);

const getSubcategoryTranslation = (subcategory: CropSubcategory): string => {
  const translation = subcategoryTranslations.get(subcategory);
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(translation, `Unknown subcategory: ${subcategory}`);
  return translation();
};

const fertilizerSubcategoryOrder: CropSubcategory[] = [
  CropSubcategory.Nitrogen,
  CropSubcategory.NitrogenSulphur,
  CropSubcategory.Phosphate,
  CropSubcategory.Potassium,
  CropSubcategory.Magnesium,
  CropSubcategory.Calcareous,
  CropSubcategory.MultiComponent,
  CropSubcategory.Foliar,
  CropSubcategory.Other,
  CropSubcategory.Bioactivcz,
];

const getSubcategoryOrder = (subcategory: CropSubcategory): number => {
  const index = fertilizerSubcategoryOrder.indexOf(subcategory);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const categorySubcategories = new Map<CropCategory, CropSubcategory[]>();

categorySubcategories.set(CropCategory.Fertilizer, [
  CropSubcategory.Bioactivcz,
  CropSubcategory.Calcareous,
  CropSubcategory.Foliar,
  CropSubcategory.Magnesium,
  CropSubcategory.MultiComponent,
  CropSubcategory.Nitrogen,
  CropSubcategory.NitrogenSulphur,
  CropSubcategory.Other,
  CropSubcategory.Phosphate,
  CropSubcategory.Potassium,
]);

categorySubcategories.set(CropCategory.PlantProtection, [
  CropSubcategory.Fungicides,
  CropSubcategory.Herbicides,
  CropSubcategory.Insecticides,
  CropSubcategory.AuxiliaryPreparations,
  CropSubcategory.GrowthRegulators,
  CropSubcategory.Rodenticides,
  CropSubcategory.Packages,
]);

const getSubcategoriesForCategory = (
  category: CropCategory,
): CropSubcategory[] => {
  return categorySubcategories.get(category) ?? [];
};

const categoryToSlug = (category: CropCategory): string => {
  return category.toLowerCase().replace(/_/g, "-");
};

const slugToCategory = (slug: string): CropCategory | null => {
  const enumValue = slug.toUpperCase().replace(/-/g, "_");
  if (Object.values(CropCategory).includes(enumValue as CropCategory)) {
    return enumValue as CropCategory;
  }
  return null;
};

const subcategoryToSlug = (subcategory: CropSubcategory): string => {
  return subcategory.toLowerCase().replace(/_/g, "-");
};

const slugToSubcategory = (slug: string): CropSubcategory | null => {
  const enumValue = slug.toUpperCase().replace(/-/g, "_");
  if (Object.values(CropSubcategory).includes(enumValue as CropSubcategory)) {
    return enumValue as CropSubcategory;
  }
  return null;
};

export {
  categoryToSlug,
  getSubcategoriesForCategory,
  getSubcategoryOrder,
  getSubcategoryTranslation,
  hasSubcategories,
  slugToCategory,
  slugToSubcategory,
  subcategoryToSlug,
};
